import {
  BitteAiChatProps,
  UnifiedChatContentProps,
  WidgetChatProps,
} from '@bitte-ai/types';
import { generateId } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMessages } from '../../hooks/useMessages';
import { useScrollToBottom } from '../../hooks/useScrollToBottom';
import { defaultColors } from '../../lib/constants';

import { shortenAddress } from '../../lib/utils';
import { useChatState } from '../../state/chat.state';
import { useAccount } from '../../state/wallet.state';
import { ChatActions } from './ChatActions';
import { ChatContainer } from './ChatContainer';
import { EnhancedChatInput } from './EnhancedChatInput';
import { MessagesList } from './MessagesList';
import { ScrollManager } from './ScrollManager';
import { ScrollToBottomButton } from './ScrollToBottomButton';
import DefaultLoadingIndicator from './default-components/DefaultLoadingIndicator';

export const UnifiedChatContent = ({
  agentId,
  options,
  customToolComponents,
  widget,
  urlChatId,
  isWidgetChat,
}: UnifiedChatContentProps) => {
  const chatId = useRef(urlChatId || generateId()).current;
  const { accountId, evmAddress } = useAccount();

  // Use the shared chat state from context
  const {
    messages,
    input,
    handleInputChange,
    isLoading: isInProgress,
    handleSubmit,
    reload,
    addToolResult,
    append,
    error,
    status,
  } = useChatState();

  const {
    borderColor,
    buttonColor,
    generalBackground,
    messageBackground,
    textColor,
  } = isWidgetChat
    ? {
        borderColor: '#334155',
        buttonColor: options?.colors?.buttonColor || '#0F172A',
        generalBackground: '#0A0A0C',
        messageBackground: '#000000',
        textColor: '#FFFFFF',
      }
    : {
        ...defaultColors,
        ...options?.colors,
      };

  // Get custom components or use defaults
  const LoadingIndicator =
    options?.customComponents?.loadingIndicator || DefaultLoadingIndicator;

  // Use the new messages hook for scroll management - Fixed for auto-prompt
  const {
    containerRef: messagesRef,
    endRef: messagesEndRef,
    isAtBottom,
    scrollToBottom,
    onViewportEnter,
    onViewportLeave,
    hasSentMessage,
  } = useMessages({
    chatId,
    status:
      status === 'error'
        ? 'ready'
        : status || (isInProgress ? 'streaming' : 'ready'),
  });

  const { scrollToBottom: triggerScrollOnSubmit } = useScrollToBottom();

  // Auto-prompt state management
  const [isPromptSent, setIsPromptSent] = useState<boolean>(false);
  const [isAutoPromptMode, setIsAutoPromptMode] = useState<boolean>(false);

  // Reset prompt state when chat changes
  useEffect(() => {
    setIsPromptSent(false);
    setIsAutoPromptMode(false);
  }, [chatId]);

  const scrollToBottomHandler = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  // Auto-prompt logic - prevent firing until chat is properly initialized
  useEffect(() => {
    if (
      options?.prompt &&
      messages &&
      messages.length === 0 &&
      !isInProgress &&
      !isWidgetChat &&
      !isPromptSent &&
      !error && // Don't fire if there's an error
      status === 'ready' && // Only fire when status is ready
      append && // Ensure append function exists
      typeof append === 'function' // Ensure append is a real function, not dummy
    ) {
      setIsPromptSent(true);
      setIsAutoPromptMode(true);
      append({
        id: generateId(),
        role: 'user',
        content: options.prompt,
      });
    }
  }, [
    options?.prompt,
    messages?.length,
    isInProgress,
    isWidgetChat,
    isPromptSent,
    error,
    status,
    append,
  ]);

  // Scroll behavior for submissions
  useEffect(() => {
    if (status === 'submitted' && !isAutoPromptMode) {
      triggerScrollOnSubmit();
    }
    if (status === 'streaming' && isAutoPromptMode) {
      setIsAutoPromptMode(false);
    }
  }, [status, triggerScrollOnSubmit, isAutoPromptMode]);

  // Memoize stable functions
  const stableAddToolResult = useCallback(addToolResult, [addToolResult]);
  const stableAppend = useCallback(append, [append]);

  // Message throttling for auto-prompt
  const isAutoPromptInitialPhase =
    options?.prompt &&
    messages &&
    messages.length <= 2 &&
    (status === 'streaming' || status === 'submitted');

  const [throttledMessages, setThrottledMessages] = useState(messages);

  useEffect(() => {
    if (options?.prompt && status === 'streaming' && isAutoPromptInitialPhase) {
      const timer = setTimeout(() => {
        setThrottledMessages(messages);
      }, 150);
      return () => clearTimeout(timer);
    } else {
      setThrottledMessages(messages);
    }
  }, [messages, options?.prompt, status, isAutoPromptInitialPhase]);

  const effectiveMessages =
    options?.prompt && status === 'streaming' && isAutoPromptInitialPhase
      ? throttledMessages
      : messages;

  // Scroll padding throttling
  const [shouldApplyScrollPadding, setShouldApplyScrollPadding] =
    useState<boolean>(false);

  useEffect(() => {
    if (options?.prompt && status === 'streaming' && isAutoPromptInitialPhase) {
      const timer = setTimeout(() => {
        setShouldApplyScrollPadding(hasSentMessage);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShouldApplyScrollPadding(hasSentMessage);
    }
  }, [hasSentMessage, options?.prompt, status, isAutoPromptInitialPhase]);

  // Memoized message props
  const memoizedMessages = useMemo(() => {
    if (!effectiveMessages || effectiveMessages.length === 0) return [];

    return effectiveMessages.map((message, index) => ({
      meta: {
        chatId,
        accountId: accountId || shortenAddress(evmAddress) || '',
        creator: accountId || undefined,
        agentName: options?.agentName,
        agentImage: options?.agentImage,
      },
      message: message,
      isLoading:
        status === 'streaming' && effectiveMessages.length - 1 === index,
      theme: {
        messageBackgroundColor: messageBackground,
        borderColor,
        textColor,
        showBorder: isWidgetChat,
        requiresScrollPadding: !options?.prompt
          ? hasSentMessage && index === effectiveMessages.length - 1
          : shouldApplyScrollPadding && index === effectiveMessages.length - 1,
        isWidgetMode: isWidgetChat,
        hasAutoPrompt: !!options?.prompt,
      },
      actions: {
        addToolResult: stableAddToolResult,
        append: stableAppend,
        hideToolCall: options?.hideToolCall || false,
      },
      customComponents: {
        messageContainer: options?.customComponents?.messageContainer,
        toolComponents: customToolComponents,
      },
    }));
  }, [
    effectiveMessages,
    chatId,
    accountId,
    evmAddress,
    options?.agentName,
    options?.agentImage,
    options?.prompt,
    status,
    messageBackground,
    borderColor,
    textColor,
    isWidgetChat,
    hasSentMessage,
    stableAddToolResult,
    stableAppend,
    options?.hideToolCall,
    options?.customComponents?.messageContainer,
    customToolComponents,
    shouldApplyScrollPadding,
  ]);

  // Create scroll-to-bottom button
  const scrollToBottomButton = (
    <ScrollToBottomButton
      isVisible={!isAtBottom}
      onClick={scrollToBottomHandler}
      generalBackground={generalBackground}
      textColor={textColor}
    />
  );

  // Create share modal for non-widget chat
  const shareModal =
    !isWidgetChat && effectiveMessages?.length > 0 ? (
      <ChatActions messages={effectiveMessages} isWidgetChat={false} />
    ) : null;

  // Create messages area (for widget chat separation)
  const messagesArea = (
    <ScrollManager
      messagesEndRef={messagesEndRef}
      onViewportEnter={onViewportEnter}
      onViewportLeave={onViewportLeave}
      containerRef={messagesRef}
    >
      {/* Messages List with scroll container - reduced padding */}
      <div
        ref={messagesRef}
        className="bitte-flex bitte-w-full bitte-flex-col bitte-gap-6"
        style={{ color: textColor }}
      >
        <MessagesList
          messages={effectiveMessages}
          isWidgetChat={isWidgetChat || false}
          isInProgress={isInProgress}
          status={status}
          error={error}
          textColor={textColor}
          memoizedMessages={memoizedMessages}
          evmAddress={evmAddress}
          // @ts-ignore
          options={options}
          widget={widget}
          onAppend={stableAppend}
          onReload={reload}
          LoadingIndicator={LoadingIndicator}
        />
        {/* End ref for scroll-to-bottom functionality */}
        <div
          ref={messagesEndRef}
          className="bitte-shrink-0 bitte-min-h-[1px]"
        />
      </div>
    </ScrollManager>
  );

  // Create input area
  const inputArea = (
    <EnhancedChatInput
      isWidgetChat={isWidgetChat || false}
      input={input}
      handleInputChange={handleInputChange}
      handleSubmit={handleSubmit}
      isInProgress={isInProgress || false}
      generalBackground={generalBackground}
      borderColor={borderColor}
      textColor={textColor}
      buttonColor={buttonColor}
      options={options}
    />
  );

  return (
    <ChatContainer
      isWidgetChat={isWidgetChat || false}
      generalBackground={generalBackground}
      borderColor={borderColor}
      agentId={agentId}
      customChatContainer={options?.customComponents?.chatContainer}
      scrollToBottomButton={scrollToBottomButton}
      shareModal={shareModal}
      messagesArea={messagesArea}
      inputArea={inputArea}
    />
  );
};

export const ChatContent = (
  props: BitteAiChatProps & { urlChatId: string },
) => <UnifiedChatContent {...props} />;

export const WidgetChatContent = (
  props: BitteAiChatProps & {
    widget: WidgetChatProps;
  },
) => <UnifiedChatContent {...props} />;
